import { FC } from 'react'
import { ScrollableTable, Td, Th, Tr } from 'components/tables'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { DateTime } from 'luxon'
import EmptyBox from './empty-box.svg'
import { Button, IconButton, MenuButton } from 'components/buttons'
import { Spinner } from 'components/loaders'
import { useDateTimeToLocaleString as useDateTimeToLocaleStringOriginal } from '@hooks/useDateTimeWithLocale'
import { Label } from 'components/indicators/label'
import { useTranslation } from 'react-i18next'
import {
  faBan,
  faCheck,
  faEllipsisH,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons'

interface ApiKey {
  createdAt: DateTime
  id: number
  label: string
  status: 'active' | 'disabled'
}

interface Props {
  keys: ApiKey[]
  onCreateKey: () => void
  onDisable: (keyId: number) => void
  onEnable: (keyId: number) => void
  onRemove: (keyId: number) => void
  pending?: boolean
  useDateTimeToLocaleString?: typeof useDateTimeToLocaleStringOriginal
}

const columnHelper = createColumnHelper<ApiKey>()

export const ApiKeys: FC<Props> = ({
  keys,
  onCreateKey,
  onDisable,
  onEnable,
  onRemove,
  pending,
  useDateTimeToLocaleString = useDateTimeToLocaleStringOriginal,
}) => {
  const { t } = useTranslation()
  const toLocaleString = useDateTimeToLocaleString()
  const columns = [
    columnHelper.accessor('label', {
      header: t('components.apiKeys.apiKey'),
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('status', {
      header: t('common.status'),
      cell: ({ getValue }) => {
        const status = getValue()
        const active = status === 'active'
        const translatedStatus = t(`components.apiKeys.status.${status}`)
        return (
          <Label color={active ? 'success' : 'neutral'}>
            {translatedStatus}
          </Label>
        )
      },
    }),
    columnHelper.accessor('createdAt', {
      header: t('components.apiKeys.createdOn'),
      cell: ({ getValue, row }) => {
        const date = getValue()
        const id = row.original.id
        return (
          <div className="flex flex-row justify-between items-center">
            {toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)(date)}
            <MenuButton
              button={({ disabled, toggleMenuHidden }) => (
                <IconButton
                  disabled={disabled}
                  onClick={toggleMenuHidden}
                  icon={faEllipsisH}
                />
              )}
              buttonVariant="subtle"
              menuItems={
                row.original.status === 'active'
                  ? [
                      {
                        icon: faBan,
                        label: t('components.apiKeys.disableKey'),
                        onClick: () => onDisable(id),
                      },
                    ]
                  : [
                      {
                        icon: faCheck,
                        label: t('components.apiKeys.enableKey'),
                        onClick: () => onEnable(id),
                      },
                      {
                        icon: faTrash,
                        label: t('components.apiKeys.removeKey'),
                        onClick: () => onRemove(id),
                      },
                    ]
              }
            />
          </div>
        )
      },
    }),
  ]

  const table = useReactTable({
    data: keys,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  return (
    <div className="flex flex-col min-h-[250px]">
      <ScrollableTable
        header={table.getHeaderGroups().map((headerGroup) => (
          <Tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <Th
                  key={header.id}
                  clickable={header.column.getCanSort()}
                  label={flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                  onClick={header.column.getToggleSortingHandler()}
                  sortDir={header.column.getIsSorted()}
                />
              )
            })}
          </Tr>
        ))}
        body={table.getRowModel().rows.map((row) => {
          return (
            <Tr key={row.id}>
              {row.getVisibleCells().map((cell) => {
                return (
                  <Td key={cell.id} className="p-4">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                )
              })}
            </Tr>
          )
        })}
        extraContent={
          keys.length === 0 ? (
            <div className="flex flex-col items-center text-center justify-center w-full h-full p-10 gap-4 min-h-[350px]">
              {pending ? (
                <Spinner />
              ) : (
                <>
                  <img
                    src={EmptyBox}
                    alt={t('components.apiKeys.noApiKeys')}
                    className="w-28"
                  />
                  <p>{t('components.apiKeys.noApiKeys')}</p>
                  <p className="text-sm max-w-60">
                    {t('components.apiKeys.createKeyDescription')}
                  </p>
                  <Button onClick={onCreateKey}>
                    {t('components.apiKeys.createKey')}
                  </Button>
                </>
              )}
            </div>
          ) : undefined
        }
      />
    </div>
  )
}
